import { createSlice } from '@reduxjs/toolkit'

import { IModifiedPlan } from '../../types/modifiedPlan.interface'
import { Filter } from '../../types/planListings.interface'

export interface PLAN_LISTING {
  applyComparisonStyle: boolean
  closestPlansCount: number
  compareBasePlan: IModifiedPlan
  favorites: string[]
  planFilters: Filter
  planIdList: string[]
  propagateDestinationIds: string[]
  propagateSourcePlan: IModifiedPlan
  selectedClosestPlanIds: string[]
  selectedClosestPlans: IModifiedPlan[]
  selectedPlanIds: string[]
  selectedPlans: IModifiedPlan[]
  showCompare: boolean
  showPropagate: boolean
  singleTabValue: string
}

const planListFilterInitialState: Filter = {
  additionalRooms: [],
  exteriorCladding: [],
  exteriorWallConstruction: [],
  maximumSqft: '',
  minimumSqft: '',
  productDepthFtMax: '',
  productDepthFtMin: '',
  productDepthInMax: '',
  productDepthInMin: '',
  productWidthFtMax: '',
  productWidthFtMin: '',
  productWidthInMax: '',
  productWidthInMin: '',
  selectedPlans: [],
}

export const planListInitialState: PLAN_LISTING = {
  applyComparisonStyle: false,
  closestPlansCount: 0,
  compareBasePlan: {} as IModifiedPlan,
  favorites: [],
  planFilters: planListFilterInitialState,
  planIdList: [],
  propagateDestinationIds: [],
  propagateSourcePlan: {} as IModifiedPlan,
  selectedClosestPlanIds: [],
  selectedClosestPlans: [],
  selectedPlanIds: [],
  selectedPlans: [],
  showCompare: false,
  showPropagate: false,
  singleTabValue: 'ALL_PLANS',
}
type FilterValue<K extends keyof Filter> = Filter[K]

export const planListingSlice = createSlice({
  initialState: planListInitialState,
  name: 'planListing',
  reducers: {
    addOrUpdateFilterItem: <K extends keyof Filter>(
      state: PLAN_LISTING,
      action: { payload: { property: K; value: FilterValue<K> } }
    ) => {
      state.planFilters[action.payload.property] = action.payload.value
    },
    patchFilter: (state, action) => {
      state.planFilters = { ...state.planFilters, ...action.payload }
    },
    patchSelectedPlans: (state, action) => {
      state.selectedPlans = state.selectedPlans.map((plan) =>
        plan.id === action.payload.planId
          ? { ...plan, _etag: action.payload._etag }
          : plan
      )
    },
    removeAllFilters: (state) => {
      state.planFilters = planListFilterInitialState
    },
    resetApplyComparisonStyle: (state) => {
      state.applyComparisonStyle = false
    },
    resetCompareBasePlan: (state) => {
      state.compareBasePlan = {} as IModifiedPlan
      state.showCompare = false
    },
    // Resets the transient listing UI state (selection, filters, compare and
    // propagate flows). Deliberately preserves `favorites` (server-mirrored
    // data that would visibly flicker off every card) and `singleTabValue`
    // (the navigation that dispatches this sets the tab itself right after).
    // The previous implementation assigned to the local parameter, so this
    // action had been a silent no-op since it was introduced.
    resetPlanListing: (state) => {
      state.applyComparisonStyle = planListInitialState.applyComparisonStyle
      state.closestPlansCount = planListInitialState.closestPlansCount
      state.compareBasePlan = planListInitialState.compareBasePlan
      state.planFilters = planListInitialState.planFilters
      state.planIdList = planListInitialState.planIdList
      state.propagateDestinationIds =
        planListInitialState.propagateDestinationIds
      state.propagateSourcePlan = planListInitialState.propagateSourcePlan
      state.selectedClosestPlanIds = planListInitialState.selectedClosestPlanIds
      state.selectedClosestPlans = planListInitialState.selectedClosestPlans
      state.selectedPlanIds = planListInitialState.selectedPlanIds
      state.selectedPlans = planListInitialState.selectedPlans
      state.showCompare = planListInitialState.showCompare
      state.showPropagate = planListInitialState.showPropagate
    },
    resetPropagateSourcePlan: (state) => {
      state.propagateDestinationIds = []
      state.propagateSourcePlan = {} as IModifiedPlan
      state.showPropagate = false
    },
    setApplyComparisonStyle: (state, action) => {
      state.applyComparisonStyle = action.payload
    },
    setClosestPlansCount: (state, action) => {
      state.closestPlansCount = action.payload
    },
    setCompareBasePlan: (state, action) => {
      state.compareBasePlan = action.payload
      state.showCompare = true
    },
    setFavorites: (state, action) => {
      state.favorites = action.payload
    },
    setProductDepthInMax: (state, action) => {
      state.planFilters.productDepthInMax = action.payload
    },
    setProductDepthInMin: (state, action) => {
      state.planFilters.productDepthInMin = action.payload
    },
    setProductWidthInMax: (state, action) => {
      state.planFilters.productWidthInMax = action.payload
    },
    setProductWidthInMin: (state, action) => {
      state.planFilters.productWidthInMin = action.payload
    },
    setPropagateDestinationIds: (state, action) => {
      state.propagateDestinationIds = action.payload
    },
    setPropagateSourcePlan: (state, action) => {
      state.propagateSourcePlan = action.payload
      state.showPropagate = true
    },
    setSelectedClosestPlanIds: (state, action) => {
      state.selectedClosestPlanIds = action.payload
    },
    setSelectedClosestPlans: (state, action) => {
      state.selectedClosestPlans = action.payload
    },
    setSelectedPlanIds: (state, action) => {
      state.selectedPlanIds = action.payload
    },
    setSelectedPlans: (state, action) => {
      state.selectedPlans = action.payload
    },
    setSinglePlansPageTab: (state, action) => {
      state.singleTabValue = action.payload
    },
  },
})

export const {
  addOrUpdateFilterItem,
  patchFilter,
  patchSelectedPlans,
  removeAllFilters,
  resetApplyComparisonStyle,
  resetCompareBasePlan,
  resetPlanListing,
  resetPropagateSourcePlan,
  setApplyComparisonStyle,
  setClosestPlansCount,
  setCompareBasePlan,
  setFavorites,
  setProductDepthInMax,
  setProductDepthInMin,
  setProductWidthInMax,
  setProductWidthInMin,
  setPropagateDestinationIds,
  setPropagateSourcePlan,
  setSelectedClosestPlanIds,
  setSelectedClosestPlans,
  setSelectedPlanIds,
  setSelectedPlans,
  setSinglePlansPageTab,
} = planListingSlice.actions
